// src/App.jsx
import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import HomePage from './pages/HomePage';
import LearnPage from './pages/LearnPage';
import CreatePage from './pages/CreatePage';
import MessagePage from './pages/MessagePage';
import ProfilePage from './pages/ProfilePage';
import BottomNav from './components/BottomNav';

function App() {
  const [currentPage, setCurrentPage] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  // 新增：用于触发 CreatePage 直接打开 Preview 的信号（使用时间戳确保每次点击都触发）
  const [createPreviewTrigger, setCreatePreviewTrigger] = useState(0);

  const containerRef = useRef(null);
  const startXRef = useRef(0);
  const currentOffsetRef = useRef(0);

  // 处理从首页“做同款”跳转到创作页并打开预览
  const handleJumpToCreatePreview = () => {
    setCurrentPage(2); // 切换到 CreatePage (索引为2)
    setCreatePreviewTrigger(Date.now()); // 更新时间戳，触发 CreatePage 的 useEffect
  };

  const pages = [
    { component: HomePage, name: '首页' },
    { component: LearnPage, name: '学曲' },
    { component: CreatePage, name: '创作' },
    { component: MessagePage, name: '消息' },
    { component: ProfilePage, name: '我的' }
  ];

  const handleStart = (e, clientX) => {
    if (e.target.closest('.h-scrollable') || e.target.closest('.remix-page') || e.target.closest('.advanced-remix-page')) {
      return;
    }
    setIsDragging(true);
    startXRef.current = clientX;
    // 拖拽开始时，基准偏移量是当前页面的位置
    currentOffsetRef.current = -currentPage * window.innerWidth;
    setDragOffset(currentOffsetRef.current);
  };

  const handleMove = (clientX) => {
    if (!isDragging) return;
    const diff = clientX - startXRef.current;
    
    // 计算基于当前页面的新偏移量
    const startPageOffset = -currentPage * window.innerWidth;
    const newOffset = startPageOffset + diff;
    
    const maxOffset = 0;
    const minOffset = -(pages.length - 1) * window.innerWidth;
    const clampedOffset = Math.max(minOffset, Math.min(maxOffset, newOffset));
    
    // 更新 Ref 以供 handleEnd 读取最新值
    currentOffsetRef.current = clampedOffset;
    // 更新 State 以触发渲染
    setDragOffset(clampedOffset);
  };

  const handleEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    // 修改：阈值改为屏幕宽度的 1/2
    const threshold = window.innerWidth * 0.5;
    const currentPageOffset = -currentPage * window.innerWidth;
    
    // 关键修改：直接从 Ref 读取最终位置，而不是依赖可能闭包过期的 state
    const finalOffset = currentOffsetRef.current;
    const diff = finalOffset - currentPageOffset;
    
    let newPage = currentPage;
    
    // 判定逻辑：只有滑动距离超过阈值才切换
    if (diff < -threshold && currentPage < pages.length - 1) {
      // 向左滑（Offset变小），去下一页
      newPage = currentPage + 1;
    } else if (diff > threshold && currentPage > 0) {
      // 向右滑（Offset变大），去上一页
      newPage = currentPage - 1;
    }
    // 否则 newPage 保持不变，即回弹
    
    setCurrentPage(newPage);
    // 立即更新位置到目标页面，依靠 CSS transition 实现平滑回弹或切换
    const finalTargetOffset = -newPage * window.innerWidth;
    setDragOffset(finalTargetOffset);
    currentOffsetRef.current = finalTargetOffset;
  };
  
  const handleTouchStart = (e) => {
    handleStart(e, e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  const handleMouseDown = (e) => {
    handleStart(e, e.clientX);
  };

  const handleMouseMove = (e) => {
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    handleEnd();
  };

  useEffect(() => {
    const handleGlobalMouseMove = (e) => { if(isDragging) handleMouseMove(e); }
    const handleGlobalMouseUp = (e) => { if(isDragging) handleMouseUp(e); }

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging]);

  useEffect(() => {
    if (!isDragging) {
      setDragOffset(-currentPage * window.innerWidth);
      currentOffsetRef.current = -currentPage * window.innerWidth;
    }
  }, [currentPage]);

  return (
    <div className="app">
      <div 
        className="pages-container"
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleEnd}
        onMouseDown={handleMouseDown}
        style={{
          transform: `translateX(${dragOffset}px)`,
          transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)'
        }}
      >
        {pages.map((page, index) => {
          const PageComponent = page.component;
          const extraProps = page.name === '创作' 
            ? { previewTrigger: createPreviewTrigger } 
            : (page.name === '首页' ? { onJumpToCreate: handleJumpToCreatePreview } : {});

          return (
            <div key={index} className="page">
              <PageComponent {...extraProps} />
            </div>
          );
        })}
      </div>
      <BottomNav currentPage={currentPage} onPageChange={setCurrentPage} />
    </div>
  );
}

export default App;